"use client";

import Image from "next/image";
import { Linkedin, Users } from "lucide-react";
import { Container } from "@/components/primitives/Container";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Reveal } from "@/components/motion/Reveal";
import { getAbout } from "@/content/about";
import { useLocale } from "@/lib/i18n/provider";

/**
 * Leadership section — named person card + collective team card.
 * Replaces the previous generic team grid.
 */
export function AboutTeam() {
  const { locale } = useLocale();
  const about = getAbout(locale);

  return (
    <section className="relative py-12 md:py-16">
      <Container>
        <Reveal delay={0}>
          <Eyebrow>{about.leadership.eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.07}>
          <h2 className="mt-4 max-w-2xl text-[clamp(1.7rem,3.2vw,2.55rem)] font-bold leading-[1.08] tracking-[-0.025em] text-[var(--color-text-primary)]">
            {about.leadership.title}
          </h2>
        </Reveal>

        <ul className="mt-9 grid gap-5 md:grid-cols-2">
          {about.leadership.entries.map((entry, i) => (
            <li key={entry.name} className="h-full">
              <Reveal delay={i * 0.1} className="h-full">
                <div className="flex h-full flex-col rounded-[20px] border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-7 transition-colors hover:border-[var(--color-surface-border-strong)]">
                  {entry.type === "person" && entry.avatar ? (
                    <div className="relative h-16 w-16 overflow-hidden rounded-full border border-[var(--color-surface-border)]">
                      <Image
                        src={entry.avatar}
                        alt={entry.name}
                        fill
                        sizes="64px"
                        className="object-cover"
                      />
                    </div>
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-accent-base)]/12 text-[var(--color-accent-base)]">
                      <Users size={24} strokeWidth={1.5} />
                    </div>
                  )}

                  <div className="mt-5 flex-1">
                    <h3 className="text-base font-semibold tracking-tight text-[var(--color-text-primary)]">
                      {entry.name}
                    </h3>
                    <p className="mt-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--color-accent-base)]">
                      {entry.role}
                    </p>
                    {entry.body.trim() ? (
                      <p className="mt-4 text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
                        {entry.body}
                      </p>
                    ) : null}
                  </div>

                  {entry.type === "person" && entry.linkedin && (
                    <a
                      href={entry.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={about.leadership.linkedInAriaLabelTemplate.replace("{name}", entry.name)}
                      className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                    >
                      <Linkedin size={15} />
                      {about.leadership.linkedInLabel}
                    </a>
                  )}
                </div>
              </Reveal>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

export default AboutTeam;
